/**
 * Common SidebarHeader component for consistent header styling across sidebars
 */

import React from 'react'
import { Button, Typography } from 'antd'
import styled from 'styled-components'
import { SPACING, MEDIA } from '../../styles/theme'

const { Text } = Typography

// Styled components
const HeaderContainer = styled.div<{ $collapsed: boolean }>`
  display: flex;
  justify-content: ${(props) => (props.$collapsed ? 'center' : 'space-between')};
  align-items: center;
  padding: ${(props) => (props.$collapsed ? `${SPACING.lg} ${SPACING.sm}` : SPACING.lg)};
  border-bottom: 1px solid var(--color-border);
  gap: ${SPACING.sm};
`

const TitleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${SPACING.sm};
  flex: 1;
  min-width: 0;
`

const Title = styled(Text)`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
  min-width: 0;
`

const ToggleBtn = styled(Button)`
  &.ant-btn {
    ${MEDIA.mobile} {
      min-width: 44px;
      min-height: 44px;
      display: flex;
      align-items: center;
      justify-content: center;
    }
  }
`

// Show only on desktop
const DesktopIcon = styled.span`
  display: inline-flex;

  ${MEDIA.mobile} {
    display: none;
  }
`

// Show only on mobile
const MobileIcon = styled.span`
  display: none;

  ${MEDIA.mobile} {
    display: inline-flex;
  }
`

interface SidebarHeaderProps {
  /** Whether the sidebar is in collapsed/minimized state */
  collapsed: boolean
  /** Icon to show next to the title (only visible when not collapsed) */
  icon?: React.ReactNode
  /** Title text (only visible when not collapsed) */
  title?: string
  /** Optional tooltip or extra content next to the title */
  extra?: React.ReactNode
  /** Icon to show on desktop for the toggle button */
  desktopIcon: React.ReactNode
  /** Icon to show on mobile for the toggle button */
  mobileIcon: React.ReactNode
  /** Callback when toggle button is clicked */
  onToggle: () => void
}

export function SidebarHeader({
  collapsed,
  icon,
  title,
  extra,
  desktopIcon,
  mobileIcon,
  onToggle,
}: SidebarHeaderProps) {
  return (
    <HeaderContainer $collapsed={collapsed}>
      {!collapsed && (
        <TitleContainer>
          {icon}
          {title && <Title strong>{title}</Title>}
          {extra}
        </TitleContainer>
      )}
      <ToggleBtn
        type="text"
        icon={
          <>
            <DesktopIcon>{desktopIcon}</DesktopIcon>
            <MobileIcon>{mobileIcon}</MobileIcon>
          </>
        }
        onClick={onToggle}
        size="small"
      />
    </HeaderContainer>
  )
}
